import { Typography, styled } from '@material-ui/core';
import React, { PropsWithChildren, ReactNode, forwardRef } from 'react';
import { IconError, IconInfo, IconSuccess, IconWarning } from '../icons';
import { NkSnackbarProps } from './types/NkSnackbarProps';
import { SnackbarType } from './types/snackbarType.enum';

export const NkSnackbar = forwardRef<HTMLDivElement, NkSnackbarProps>(
  (props, ref) => {
    return (
      <SnackbarBackground ref={ref} style={props.style}>
        <SnackbarHeader icon={getSnackBarIcon(props.variant)}>
          <Typography variant="h6">{props.message}</Typography>
        </SnackbarHeader>
        <StyledAdditionalData>{props.data}</StyledAdditionalData>
        {props.action && (
          <StyledActionsContainer>
            {SnackbarActions(props)}
          </StyledActionsContainer>
        )}
      </SnackbarBackground>
    );
  },
);

/**
 * Used to pass the props to whatever action component we specify
 */
const SnackbarActions = ({
  action,
  ...props
}: NkSnackbarProps): JSX.Element => {
  if (React.isValidElement(action)) {
    return React.cloneElement(action, props);
  } else {
    return <>{action}</>;
  }
};

const SnackbarBackground = styled('div')(({ theme }) => ({
  // TODO: change this to appropriate theme color once theming is done on app level
  backgroundColor: theme.palette.background.default,
  maxWidth: '427px',
  borderRadius: '15px',
  padding: '14px 20px',
}));

function getSnackBarIcon(iconType?: SnackbarType): ReactNode | undefined {
  switch (iconType) {
    case SnackbarType.error:
      return <IconError />;
    case SnackbarType.warning:
      return <IconWarning />;
    case SnackbarType.success:
      return <IconSuccess />;
    case SnackbarType.info:
      return <IconInfo />;
    default:
      return undefined;
  }
}

const SnackbarHeader = ({
  icon,
  children,
}: PropsWithChildren<{ icon?: ReactNode }>) => {
  return (
    <StyledHeaderWrapper>
      {icon && <StyledIconWrapper>{icon}</StyledIconWrapper>}
      {children}
    </StyledHeaderWrapper>
  );
};

const StyledHeaderWrapper = styled('div')({
  display: 'flex',
  flexFlow: 'row nowrap',
  alignItems: 'center',
});

const StyledIconWrapper = styled('span')({
  display: 'inline-flex',
  marginRight: '20px',
});

const StyledAdditionalData = styled('div')({
  marginTop: '10px',
});

const StyledActionsContainer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: '15px',
});
